import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "./ui/button";
import { WorkflowInstructionPlaybook } from "./WorkflowInstructionPlaybook";
import { WorkflowShare } from "./WorkflowShare";
import type { LibraryEntry } from "@/data/aiWorkflows";
import {
  FULL_REVIEW_RECORD,
  REVIEW_RECORD,
  REVIEW_QUESTIONS,
} from "@/data/promptLibraryMethod";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/utils/posthog";
import { PromptLibraryAI30Invitation } from "./PromptLibraryAI30";

type CopyKind =
  | "first_run"
  | "review_record"
  | "full_review_record"
  | "system_instructions"
  | "example_prompt";
const COPY_ERRORS: Record<CopyKind, string> = {
  first_run:
    "Copy didn’t work. Expand View full prompt below and copy that text manually.",
  review_record:
    "Copy didn’t work. Select the three review questions below and copy them manually.",
  full_review_record:
    "Copy didn’t work. Expand Compare recurring work below and copy the detailed record manually.",
  system_instructions:
    "Copy didn’t work. Expand View full instructions as text under Use this again and copy that text manually.",
  example_prompt:
    "Copy didn’t work. Select the practice example under Use this again and copy it manually.",
};

export function WorkflowPractice({
  entry,
  standalone = false,
}: {
  entry: LibraryEntry;
  standalone?: boolean;
}) {
  const StepHeading = standalone ? "h3" : "h4";
  const [copied, setCopied] = useState<CopyKind | null>(null);
  const [copyError, setCopyError] = useState<CopyKind | null>(null);
  const copy = async (text: string, kind: CopyKind) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setCopyError(null);
      trackEvent("AI Top 40 Prompt Copied", {
        copy_type: kind,
        workflow: entry.title,
        workflow_slug: entry.slug,
        category: entry.category.id,
        source_page: "fortune-100-prompt-library",
      });
    } catch {
      setCopyError(kind);
      setCopied(null);
    }
  };
  const copyButton = (text: string, kind: CopyKind, label: string) => (
    <Button
      type="button"
      variant={kind === "first_run" ? "default" : "outline"}
      className={cn(
        "h-auto min-h-11 max-w-full whitespace-normal",
        kind === "first_run"
          ? "bg-longhand-accent text-longhand-paper hover:bg-longhand-accent-hover"
          : "border-longhand-accent bg-longhand-paper text-longhand-accent hover:bg-longhand-accent-light",
      )}
      onClick={() => copy(text, kind)}
      aria-label={`${label} for ${entry.title}`}
    >
      {copied === kind ? (
        <Check className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
      ) : (
        <Copy className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      {copied === kind ? "Copied" : label}
    </Button>
  );
  return (
    <div className="min-w-0 space-y-8 border-t border-longhand-border bg-longhand-paper px-5 py-6 sm:px-7 sm:py-8">
      <div className="grid gap-6 md:grid-cols-2">
        <section aria-label="Required inputs">
          <StepHeading className="font-semibold">1. Bring this</StepHeading>
          <p className="mt-2 text-sm text-longhand-ink/80">
            Use an approved AI workspace and sources you&apos;re allowed to
            share. Pasted notes or attached files are enough for the first run.
          </p>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-longhand-ink">
            {entry.requiredInputs.map((input) => (
              <li key={input}>{input}</li>
            ))}
          </ul>
          {entry.optionalInputs.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold">Optional context</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-longhand-ink/80">
                {entry.optionalInputs.map((input) => (
                  <li key={input}>
                    {input.replace(
                      /\s*\((?:optional|if available|for context)\)/gi,
                      "",
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
        <section aria-label="Expected result">
          <StepHeading className="font-semibold">A good result</StepHeading>
          <ul className="mt-2 list-disc space-y-2 pl-5 text-sm text-longhand-ink/80">
            {entry.outputSummary.map((output) => (
              <li key={output}>{output}</li>
            ))}
          </ul>
          {entry.templateSource && (
            <p className="mt-3 text-sm text-longhand-ink/80">
              Template created at {entry.templateSource}. The practice example
              below is fictional.
            </p>
          )}
        </section>
      </div>

      <section aria-label="First-run prompt" className="min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <StepHeading className="font-semibold">2. Try it</StepHeading>
            <p className="mt-2 text-sm text-longhand-ink/80">
              Copy this prompt into Claude or ChatGPT. Replace the brackets and
              add your sources.
            </p>
          </div>
          {copyButton(entry.firstRunPrompt, "first_run", "Copy prompt")}
        </div>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-longhand-ink">
          {entry.promptPreview}
        </p>
        <p className="mt-3 max-w-3xl text-sm text-longhand-ink/80">
          The prompt asks for a draft from your sources. You check it before
          use; sending, publishing, and record changes are outside this run.
        </p>
        <details className="mt-3">
          <summary className="min-h-11 cursor-pointer py-2 text-sm font-semibold underline underline-offset-4">
            View full prompt
          </summary>
          <pre
            tabIndex={0}
            className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-longhand-border p-4 font-sans text-sm leading-relaxed text-longhand-ink"
          >
            {entry.firstRunPrompt}
          </pre>
        </details>
        <p
          role="status"
          aria-live="polite"
          className="mt-3 text-sm text-longhand-ink"
        >
          {copyError
            ? COPY_ERRORS[copyError]
            : copied
              ? "Copied. Paste into your own AI workspace or review document."
              : ""}
        </p>
      </section>

      <section aria-label="Acceptance checks">
        <StepHeading className="font-semibold">3. Check it</StepHeading>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-longhand-ink">
          {entry.reviewChecks.map((check) => (
            <li key={check}>{check}</li>
          ))}
        </ul>
      </section>

      <section
        aria-label="Improve the next run"
        className="border-t border-longhand-border pt-6"
      >
        <StepHeading className="font-semibold">4. Improve it</StepHeading>
        <p className="mt-2 max-w-3xl text-sm text-longhand-ink/80">
          Keep one correction and one accepted change for the next run.
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-longhand-ink">
          {REVIEW_QUESTIONS.map((question) => (
            <li key={question}>{question}</li>
          ))}
        </ul>
        <div className="mt-4">
          {copyButton(REVIEW_RECORD, "review_record", "Copy review record")}
        </div>
        <details className="mt-4">
          <summary className="min-h-11 cursor-pointer py-2 text-sm font-semibold underline underline-offset-4">
            Compare recurring work
          </summary>
          <p className="mt-2 max-w-3xl text-sm text-longhand-ink/80">
            Use similar work and the same checks before and after. Count
            prompting, reviewing, and correcting time, plus setup and
            maintenance effort. Check quality and missed steps alongside time.
            Record unknown measurements as unknown and keep estimates separate
            from observed results. The review record includes these optional
            fields.
          </p>
          <pre
            tabIndex={0}
            className="my-3 overflow-auto whitespace-pre-wrap break-words font-sans text-sm text-longhand-ink"
          >
            {FULL_REVIEW_RECORD}
          </pre>
          {copyButton(
            FULL_REVIEW_RECORD,
            "full_review_record",
            "Copy detailed review record",
          )}
        </details>
      </section>

      <PromptLibraryAI30Invitation workflow={entry} />
      <details className="border-t border-longhand-border pt-3">
        <summary className="min-h-11 cursor-pointer py-3 font-semibold underline underline-offset-4">
          Use this again
        </summary>
        <div className="mt-4 min-w-0 space-y-5">
          <p className="text-sm text-longhand-ink/80">
            After a reviewed run, keep the instructions, approved sources,
            checks, and accepted examples together. Try them in a fresh
            conversation. Another authorized colleague should be able to use
            them and recognize a good result.
          </p>
          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-semibold">Claude</dt>
              <dd className="mt-1 text-longhand-ink/80">
                Save the instructions in a dedicated Project. Package them as a
                native Skill when your approved workspace supports it.
              </dd>
            </div>
            <div>
              <dt className="font-semibold">ChatGPT</dt>
              <dd className="mt-1 text-longhand-ink/80">
                Save the instructions in a dedicated Project or your
                workspace&apos;s equivalent. Keep the sources and reviewed
                examples with it.
              </dd>
            </div>
          </dl>
          <p className="text-sm text-longhand-ink/80">
            Repeat manually until the results and checks hold up. The
            accountable owner approves any schedule, wider access, or external
            action.
          </p>
          {entry.crossToolNote && (
            <p className="text-sm text-longhand-ink/80">
              Full playbook setup: {entry.crossToolNote}
            </p>
          )}
          <a
            href="/ai-30/program/prep"
            className="inline-flex min-h-11 items-center font-semibold text-longhand-accent underline"
          >
            Need help preparing your workspace?
          </a>
          <WorkflowInstructionPlaybook
            workflowTitle={entry.title}
            instructions={entry.systemInstructions}
            copied={copied === "system_instructions"}
            onCopy={() => copy(entry.systemInstructions, "system_instructions")}
          />
          <details>
            <summary className="min-h-11 cursor-pointer py-2 text-sm font-semibold underline">
              View full instructions as text
            </summary>
            <pre
              tabIndex={0}
              className="mt-3 overflow-auto whitespace-pre-wrap break-words font-sans text-sm text-longhand-ink"
            >
              {entry.systemInstructions}
            </pre>
          </details>
          <details>
            <summary className="min-h-11 cursor-pointer py-2 text-sm font-semibold underline">
              See a practice example
            </summary>
            <p className="mt-2 text-sm text-longhand-ink/80">
              Use this as an illustration. Supply your own approved sources; an
              example is not evidence about a real company.
            </p>
            <pre
              tabIndex={0}
              className="my-3 overflow-auto whitespace-pre-wrap break-words font-sans text-sm text-longhand-ink"
            >
              {entry.examplePrompt}
            </pre>
            {copyButton(
              entry.examplePrompt,
              "example_prompt",
              "Copy practice example",
            )}
          </details>
          <WorkflowShare
            workflow={entry}
            placement="after_copy"
            label="Share this workflow"
          />
        </div>
      </details>
    </div>
  );
}
